<script setup>
import { useForm } from 'vee-validate'
import { storeToRefs } from 'pinia'
import { computed } from 'vue'
import * as yup from 'yup'
import { useAuthStore } from '~/stores/auth'
import { useMessageStore } from '~/stores/messages'
import { checkCustomerAge, checkDateFormat } from '~/utils/validators'

// Fetch messages
const messagesStore = useMessageStore()
const { messages: staticKeys } = storeToRefs(messagesStore)

// Auth store helpers
const authStore = useAuthStore()

const {
    updateUserStatus,
    updateProfileSuccessCallback,
    updateProfileErrorCallback,
    updateProfileOnSettledCallback,
    updateProfileOnMutateCallback
} = authStore

const { userStatus } = storeToRefs(authStore)

// Fetch route
const route = useRoute()
const centerRoute = computed(() => route.params?.centerUid)

// Get Auth states
const bearerToken = useState('state_bearerToken')
const oid = useState('state_userOid')

// Get center details
const { data: centerDetails, suspense: centerDetailSuspense } = useFetchCenterDetails(centerRoute.value)

// Get user data
const { data: user, suspense: userSuspense, isError: isUserError } = useFetchProfile({ bearerToken, oid })

// Fetch preferences
const { data: preferences, suspense: preferencesSuspense } = useFetchConsumerPreferences()

onServerPrefetch(async () => {
    await centerDetailSuspense()
    await userSuspense()
    await preferencesSuspense()
})

// Handles error or failed server status code  response
if (isUserError.value || user.value?.status === 'failed') {
    updateUserStatus({
        success: false,
        message: staticKeys.value?.account_form_error_load,
    })
}

// Compute current user
const currentUser = computed(() => user.value?.data)

// Prepare form
const { values, meta, handleSubmit, isSubmitting } = useForm({
    firstName: '',
    lastName: '',
    birthDate: '',
    billingAddress: {
        postalCode: ''
    },
    salutationCode: '',
    preferences: ''
})

// The preferences the user might have already entered (in case of edit)
const consumerProfilePreferences = computed(() => currentUser.value?.preferences?.interests || [])

// Define config for form elements
const userPreferences = computed(() => {
    return preferences?.value?.preferences?.map(obj => ({
        value: obj.id,
        smart: true,
        id: `preferences-[${obj.id}]`,
        label: obj.name,
        name: 'preferences'
    }))
})

// Add dynamic values (use empty strings, because without this the form is not submitted)
const getFirstName = computed(() => currentUser.value?.firstName || '')
const getLastName = computed(() => currentUser.value?.lastName || '')
const getBirthday = computed(() => currentUser.value?.birthDate || '')
const getPostCode = computed(() => currentUser.value?.billingAddress?.postalCode || '')
const getGender = computed(() => currentUser.value?.salutationCode || '')
const isPending = computed(() => userIsPending.value || isSubmitting.value)

// Define config objects for form fields
const firstNameConfig = computed(() => {
    return {
        label: staticKeys.value?.account_form_firstname_label,
        fieldType: 'text',
        id: 'firstName',
        name: 'firstName',
        required: 'required',
        validationRules: yup.string()
            .required(staticKeys.value?.account_form_firstname_error_message)
            .max(40, staticKeys.value?.general_error_maxlength.replace('%s', '40')),
        pending: isPending.value,
        val: getFirstName.value
    }
})

const lastNameConfig = computed(() => {
    return {
        label: staticKeys.value?.account_form_lastname_label,
        fieldType: 'text',
        id: 'lastName',
        name: 'lastName',
        pending: isPending.value,
        val: getLastName.value
    }
})

const birthdayConfig = computed(() => {
    return {
        label: staticKeys.value?.account_form_birthday_label,
        fieldType: 'date',
        clickable: true,
        id: 'birthDate',
        name: 'birthDate',
        validationRules: yup.string()
            .test(
                'validateBirthDate',
                staticKeys.value?.account_form_birthday_error_message,
                value => checkCustomerAge(value)
            )
            .test(
                'validDateFormat',
                staticKeys.value?.general_date_validation_error_message,
                value => checkDateFormat(value)
            ),
        pending: isPending.value,
        val: getBirthday.value,
        helperText: staticKeys.value?.account_form_birthday_headline
    }
})

const postCodeConfig = computed(() => {
    return {
        label: staticKeys.value?.account_form_postalcode_label,
        placeholder: staticKeys.value?.account_form_postalcode_placeholder,
        fieldType: 'text',
        id: 'postalCode',
        name: 'postalCode',
        validationRules: yup.string()
            .matches(/^\d{5}$/, {
                excludeEmptyString: true,
                message: staticKeys.value?.account_form_postalcode_error_message
            }),
        pending: isPending.value,
        val: getPostCode.value
    }
})

const genderConfig = computed(() => {
    return {
        female: {
            label: staticKeys.value?.account_form_gender_female_label,
            name: 'salutationCode',
            id: 'female',
            value: 'female',
            val: getGender.value
        },
        male: {
            label: staticKeys.value?.account_form_gender_male_label,
            name: 'salutationCode',
            value: 'male',
            id: 'male',
            val: getGender.value
        },
        diverse: {
            label: staticKeys.value?.account_form_gender_divers_label,
            name: 'salutationCode',
            value: 'diverse',
            id: 'diverse',
            val: getGender.value
        }
    }
})

// Active state button
const isActive = computed(() => {
    if (!values.firstName) {
        return false
    }

    return meta.value?.valid
})

// As long as a status message appears don't allow active button
const isFormSubmitting = computed(() => isSubmitting.value || userStatus.value?.success)

// Get form class if a user is submitting
const getFormClass = computed(() => {
    return {
        'is-submitting': isFormSubmitting.value
    }
})

// Get submit button config
const submitButtonConfig = computed(() => {
    return {
        visible: true,
        label: staticKeys.value?.account_form_button_label_savechanges,
        buttonConfig: {
            type: 'submit',
            buttonType: 'primary',
            isPending: isFormSubmitting.value,
            large: true,
            active: isActive.value
        }
    }
})

// Callback for successful onBoarding request
const onBoardingSuccessCallback = () => {
    // Track user click
    trackUserClick({
        eventName: TRACKING_CONSTANTS.CLICK_EVENTS.USER_ONBOARDING,
        centerUid: centerDetails.value?.centerUid,
        centerName: centerDetails.value?.centerName,
        stepId: TRACKING_CONSTANTS.MISC.STEP_ID_ONBOARDING,
        stepAction: TRACKING_CONSTANTS.MISC.STEP_ACTION_ONBOARDING
    })

    trackUserClick({
        eventName: TRACKING_CONSTANTS.CLICK_EVENTS.USER_PREFERENCES,
        centerUid: centerDetails.value?.centerUid,
        centerName: centerDetails.value?.centerName,
        registerFormUserData: getFormattedTrackingUserData(values)
    })

    updateProfileSuccessCallback({
        successMessage: staticKeys.value?.account_form_personaldata_success_message,
        closeLayer: true
    })
}

// Fetch mutation
const { mutate, isPending: userIsPending } = useUpdateProfileMutation({
    bearerToken,
    oid,
    onSuccessCallback: onBoardingSuccessCallback,
    onErrorCallback: () => updateProfileErrorCallback({ errorMessage: staticKeys.value?.account_form_update_error, oid }),
    onMutateCallback: updateProfileOnMutateCallback,
    onSettledCallback: () => updateProfileOnSettledCallback({ oid }),
})

/**
 *  Handles form submit
 */
const onSubmit = handleSubmit(async (values) => {
    // Transform user object in the required format
    const transformedUserValues = getFormattedFormUserData(values)

    // Fire update request
    mutate({
        body: transformedUserValues,
        oid
    })
})

/**
 * Resets the status object with the given value.
 *
 * @param {any} value - The new value to set the status object to.
 * @return {void} This function does not return anything.
 *
 */
const resetStatusObj = (value) => {
    updateUserStatus(value)
}

// Track Page Load
trackPageLoad({
    title: TRACKING_CONSTANTS.TITLES.REGISTER_PREFERENCES,
    centerUid: centerDetails.value?.centerUid,
    centerName: centerDetails.value?.centerName
})
</script>

<template>
    <SideBarLayer>
        <SideBarWrapper>
            <form class="user-account-form-content" :class="getFormClass" @submit.prevent="onSubmit">
                <SideBarHeader>
                    <SideBarMainHeadline :sidebar-main-headline-config="{ title: staticKeys?.account_form_personaldata_headline_markdown }" />
                </SideBarHeader>

                <FormMessage :form-message-config="userStatus" @update:set-status="resetStatusObj" />

                <FormSection>
                    <p class="user-account-form-intro">
                        {{ staticKeys?.account_form_salutation_headline }}
                        <span class="user-account-form-asterisk">
                            {{ staticKeys?.account_form_asterisk }}.
                        </span>
                    </p>

                    <FormText :input-config="firstNameConfig" />
                    <FormText :input-config="lastNameConfig" />
                </FormSection>

                <FormSection>
                    <p class="user-account-form-intro">
                        {{ staticKeys?.account_form_gender_intro }}
                    </p>

                    <p class="user-account-form-label">
                        {{ staticKeys?.account_form_gender_headline }}
                    </p>

                    <FormRadioGroup>
                        <FormRadio :radio-button-config="genderConfig?.female" />
                        <FormRadio :radio-button-config="genderConfig?.male" />
                        <FormRadio :radio-button-config="genderConfig?.diverse" />
                    </FormRadioGroup>
                </FormSection>

                <FormSection>
                    <p class="user-account-form-intro">
                        {{ staticKeys?.account_form_birthday_headline }}
                    </p>

                    <FormText :input-config="birthdayConfig" />
                </FormSection>
                <FormSection>
                    <p class="user-account-form-intro">
                        {{ staticKeys?.account_form_postalcode_headline }}
                    </p>

                    <FormText :input-config="postCodeConfig" />
                </FormSection>

                <FormSection>
                    <p class="user-account-form-intro">
                        {{ staticKeys?.account_form_preferences_headline }}:
                    </p>

                    <FormCheckBoxGroup>
                        <FormCheckBox
                            v-for="item in userPreferences"
                            :key="item.id"
                            :checkbox-config="{ ...item, ...{ val: consumerProfilePreferences }, ...{ pending: isSubmitting } }"
                        />
                    </FormCheckBoxGroup>
                </FormSection>

                <FormButton :form-button-config="submitButtonConfig" />
            </form>
        </SideBarWrapper>
    </SideBarLayer>
</template>

<style lang="scss">
.user-account-form-intro {
    margin-block-end: 20px;

    @include body2-medium;
}

.user-account-form-label {
    @include form-label;
}

.user-account-form-asterisk {
    @include form-asterisk;
}

.user-account-form-content {
    &::after {
        content: '';
        transition: opacity 400ms ease;
        opacity: 0;
    }

    &.is-submitting {
        @include form-is-submitting;
    }
}
</style>
